/**
 * ExecPro360 - Page publique de réservation (type Calendly)
 * Accessible sans authentification
 * Copyright (c) 2026 Emmanuel TESTE
 */
import { useState, useEffect, useCallback } from "react";
import { PUBLISHER_LABEL, COPYRIGHT_YEAR } from "../config/legal";
import {
  Clock, Video, Phone, MapPin, Shuffle, ChevronLeft, ChevronRight,
  Check, Globe, ArrowLeft, Loader, AlertCircle, Zap,
} from "lucide-react";

const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAYS_SHORT  = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

const LOCATION_INFO = {
  visio:      { Icon: Video,  label: "Visio" },
  telephone:  { Icon: Phone,  label: "Téléphone" },
  presentiel: { Icon: MapPin, label: "Présentiel" },
  flexible:   { Icon: Shuffle,label: "Flexible" },
};

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDateFull(dateStr) {
  return new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(dateStr));
}

function formatTime(dtStr) {
  return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).format(new Date(dtStr));
}

// ── Styles ───────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0F1117",
    color: "#F9FAFB",
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: "48px 24px 90px",
  },
  container: {
    maxWidth: 1140,
    margin: "0 auto",
  },
  card: {
    background: "#1E2433",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    overflow: "hidden",
  },
  input: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: 9,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#F9FAFB",
    fontSize: 15,
    boxSizing: "border-box",
    outline: "none",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#94A3B8",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  btnPrimary: {
    background: "#1a9e72",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "15px 24px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 16,
    width: "100%",
    transition: "background 0.15s",
  },
};

// ── Composant Calendrier ───────────────────────────────────────────────────

function BookingCalendar({ availableDays, selectedDate, onSelect, onMonthChange, initialMonth }) {
  const [year, setYear] = useState(() => {
    const [y] = (initialMonth || new Date().toISOString().slice(0, 7)).split("-").map(Number);
    return y;
  });
  const [month, setMonth] = useState(() => {
    const [, m] = (initialMonth || new Date().toISOString().slice(0, 7)).split("-").map(Number);
    return m - 1;
  });

  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const blanks = firstDay === 0 ? 6 : firstDay - 1;

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    if (newMonth > 11) { newMonth = 0; newYear++; }
    setMonth(newMonth);
    setYear(newYear);
    onMonthChange(`${newYear}-${String(newMonth + 1).padStart(2, "0")}`);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button onClick={() => changeMonth(-1)} style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#94A3B8" }}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#F9FAFB" }}>{MONTH_NAMES[month]} {year}</div>
        <button onClick={() => changeMonth(1)} style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#94A3B8" }}>
          <ChevronRight size={16} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 6 }}>
        {DAYS_SHORT.map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#475569", padding: "4px 0" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {Array.from({ length: blanks }).map((_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isAvail = availableDays.includes(dateStr);
          const isPast = dateStr < today;
          const isSelected = dateStr === selectedDate;

          return (
            <div
              key={day}
              onClick={() => isAvail && !isPast && onSelect(dateStr)}
              style={{
                textAlign: "center",
                padding: "8px 4px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: isSelected ? 700 : 400,
                cursor: isAvail && !isPast ? "pointer" : "default",
                background: isSelected ? "#1a9e72" : isAvail && !isPast ? "rgba(26,158,114,0.12)" : "transparent",
                color: isSelected ? "#fff" : isPast ? "#334155" : isAvail ? "#6EE7B7" : "#475569",
                border: isSelected ? "1px solid #34D399" : isAvail && !isPast ? "1px solid rgba(26,158,114,0.3)" : "1px solid transparent",
                transition: "all 0.12s",
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page principale ──────────────────────────────────────────────────────

export default function Booking({ slug: propSlug }) {
  // Extraire le slug depuis l'URL si non fourni via prop
  const slug = propSlug || window.location.pathname.replace(/\/booking\//i, "").split("/")[0];

  const [meetingType, setMeetingType] = useState(null);
  const [step, setStep] = useState(1); // 1=date, 2=form, 3=confirm
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [rescheduling, setRescheduling]  = useState(false);

  // Vérifier si c'est une action confirm/cancel via token dans l'URL
  const urlParams = new URLSearchParams(window.location.search);
  const confirmToken = urlParams.get("token");
  const isConfirmAction    = window.location.pathname.includes("/booking/confirm");
  const isCancelAction     = window.location.pathname.includes("/booking/cancel");
  const isRescheduleAction = window.location.pathname.includes("/booking/reschedule");

  // Traiter les actions confirm/cancel
  useEffect(() => {
    if (!confirmToken) return;
    const action = isConfirmAction ? "confirm" : isCancelAction ? "cancel" : null;
    if (!action) return;

    fetch(`/api/booking/action/${action}?token=${confirmToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.ok) {
          setStep(3);
          setConfirmation({
            action,
            message: action === "confirm"
              ? "✅ Votre présence est confirmée. À bientôt !"
              : "❌ Votre rendez-vous a été annulé.",
          });
        }
        setLoading(false);
      })
      .catch(() => { setError("Erreur de traitement"); setLoading(false); });
  }, [confirmToken, isConfirmAction, isCancelAction]);

  // Charger les infos du RDV à replanifier (mode reschedule)
  useEffect(() => {
    if (!isRescheduleAction || !confirmToken) return;
    fetch(`/api/booking/reschedule/${confirmToken}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => { setMeetingType(data.meeting_type); setLoading(false); })
      .catch(() => { setError("Ce lien de replanification est invalide, expiré, ou le rendez-vous est déjà passé/annulé."); setLoading(false); });
  }, [isRescheduleAction, confirmToken]);

  // Charger les infos du type de RDV (réservation normale)
  useEffect(() => {
    if (confirmToken) return;
    if (!slug) { setError("Lien de réservation invalide."); setLoading(false); return; }

    fetch(`/api/booking/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => { setMeetingType(data); setLoading(false); })
      .catch(() => { setError("Ce lien de réservation est invalide ou inactif."); setLoading(false); });
  }, [slug, confirmToken]);

  // Base d'URL API selon le mode (réservation normale vs replanification)
  const apiBase = isRescheduleAction
    ? `/api/booking/reschedule/${encodeURIComponent(confirmToken || "")}`
    : `/api/booking/${encodeURIComponent(slug)}`;

  // Charger les jours disponibles du mois
  const loadAvailableDays = useCallback(async (month) => {
    if (isRescheduleAction ? !confirmToken : !slug) return;
    const r = await fetch(`${apiBase}/available-days?month=${month}`);
    const data = await r.json();
    setAvailableDays(data.available_days || []);
  }, [slug, isRescheduleAction, confirmToken, apiBase]);

  useEffect(() => { if (meetingType) loadAvailableDays(currentMonth); }, [meetingType, currentMonth, loadAvailableDays]);

  // Charger les créneaux d'une date
  const loadSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const r = await fetch(`${apiBase}/slots?date=${date}`);
      const data = await r.json();
      setSlots(data.slots || []);
    } finally {
      setSlotsLoading(false);
    }
  };

  // Soumission de la replanification (pas de formulaire — coordonnées déjà connues)
  const handleRescheduleSubmit = async () => {
    if (!selectedSlot) return;
    setRescheduling(true);
    try {
      const r = await fetch(`/api/booking/reschedule/${confirmToken}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_datetime: selectedSlot.start }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      setConfirmation({ ...data, isReschedule: true });
      setStep(3);
    } catch (e) {
      alert(e.message || "Erreur lors de la replanification. Réessayez.");
    } finally {
      setRescheduling(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    loadSlots(date);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !selectedSlot) return;
    setSubmitting(true);
    try {
      const r = await fetch(`/api/booking/${encodeURIComponent(slug)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_datetime: selectedSlot.start,
          guest_name: form.name,
          guest_email: form.email,
          guest_phone: form.phone,
          guest_company: form.company,
          guest_message: form.message,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      setConfirmation(data);
      setStep(3);
    } catch (e) {
      alert(e.message || "Erreur lors de la réservation. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Rendu chargement ────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...styles.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <Loader size={32} color="#1a9e72" style={{ animation: "spin 1s linear infinite" }} />
      <div style={{ color: "#64748B", fontSize: 14 }}>Chargement…</div>
    </div>
  );

  // ── Rendu erreur ────────────────────────────────────────────────────────
  if (error) return (
    <div style={{ ...styles.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <AlertCircle size={40} color="#EF4444" />
      <div style={{ color: "#EF4444", fontWeight: 600 }}>{error}</div>
    </div>
  );

  // ── Rendu confirmation action token ────────────────────────────────────
  if (step === 3 && confirmation?.action) return (
    <div style={{ ...styles.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>{confirmation.action === "confirm" ? "✅" : "❌"}</div>
        <h2 style={{ color: "#F9FAFB", marginBottom: 8 }}>{confirmation.message}</h2>
      </div>
    </div>
  );

  const LocInfo = LOCATION_INFO[meetingType?.location_type] || LOCATION_INFO.flexible;

  // ── Rendu principal ─────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 860px) {
          .pp-book-main-grid { grid-template-columns: 1fr !important; }
          .pp-book-cal-grid { grid-template-columns: 1fr !important; }
          .pp-book-card { padding: 22px !important; }
          .pp-book-form-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .pp-book-card { padding: 16px !important; }
        }
      `}</style>
      {/* Header marque */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(26,158,114,0.15)", border: "1px solid rgba(26,158,114,0.25)", borderRadius: 12, padding: "8px 16px" }}>
          <Zap size={16} color="#34D399" />
          <span style={{ color: "#34D399", fontWeight: 700, fontSize: 14 }}>ExecPro360</span>
        </div>
      </div>

      <div style={styles.container}>

        {/* ── Étape 3 — Confirmation réservation ─────────────────────────── */}
        {step === 3 && confirmation && !confirmation.action && (
          <div style={{ ...styles.card, maxWidth: 520, margin: "0 auto", padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
            <h2 style={{ color: "#F9FAFB", fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
              {confirmation.isReschedule ? "Rendez-vous replanifié !" : "Rendez-vous confirmé !"}
            </h2>
            <p style={{ color: "#94A3B8", marginBottom: 24 }}>
              Un email de confirmation vous a été envoyé à <strong style={{ color: "#F9FAFB" }}>{confirmation.guest_email || form.email}</strong>.
            </p>

            <div style={{ background: "rgba(26,158,114,0.08)", border: "1px solid rgba(26,158,114,0.2)", borderRadius: 12, padding: 16, marginBottom: 24, textAlign: "left" }}>
              <div style={{ fontWeight: 700, color: "#6EE7B7", marginBottom: 8 }}>{confirmation.meeting_type?.title}</div>
              <div style={{ color: "#94A3B8", fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
                <div>📅 {formatDateFull(selectedDate)}</div>
                <div>⏰ {formatTime(selectedSlot?.start)} (Europe/Paris)</div>
                <div>⏱ {confirmation.meeting_type?.duration_minutes} minutes</div>
              </div>
            </div>

            {confirmation.calendar_links?.google && (
              <a
                href={confirmation.calendar_links.google}
                target="_blank"
                rel="noreferrer"
                style={{ display: "block", background: "#4285F4", color: "#fff", borderRadius: 10, padding: "12px 24px", textDecoration: "none", fontWeight: 700, marginBottom: 10 }}
              >
                📅 Ajouter à Google Calendar
              </a>
            )}

            {!confirmation.isReschedule && (
              <button
                onClick={() => { setStep(1); setSelectedDate(null); setSelectedSlot(null); setConfirmation(null); setForm({ name: "", email: "", phone: "", company: "", message: "" }); }}
                style={{ background: "rgba(255,255,255,0.06)", color: "#94A3B8", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 20px", cursor: "pointer", fontSize: 13 }}
              >
                Prendre un autre rendez-vous
              </button>
            )}
          </div>
        )}

        {/* ── Étapes 1 & 2 ─────────────────────────────────────────────── */}
        {step < 3 && (
          <div className="pp-book-main-grid" style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 28, alignItems: "start" }}>

            {/* Colonne gauche — Infos RDV */}
            <div className="pp-book-card" style={{ ...styles.card, padding: 32 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a9e72", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Rendez-vous</div>
              <h2 style={{ margin: "0 0 20px", fontSize: 24, fontWeight: 800, color: "#F9FAFB", lineHeight: 1.3 }}>{meetingType?.title}</h2>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: "#94A3B8" }}>
                  <Clock size={17} color="#1a9e72" />
                  <span>{meetingType?.duration_minutes} minutes</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: "#94A3B8" }}>
                  <LocInfo.Icon size={17} color="#1a9e72" />
                  <span>{LocInfo.label}</span>
                </div>
                {meetingType?.location_details && meetingType.location_type === "visio" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#94A3B8" }}>
                    <Globe size={14} color="#1a9e72" />
                    <span style={{ wordBreak: "break-all" }}>{meetingType.location_details}</span>
                  </div>
                )}
              </div>

              {meetingType?.description && (
                <p style={{ fontSize: 15, color: "#94A3B8", lineHeight: 1.7, margin: 0, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 18 }}>
                  {meetingType.description}
                </p>
              )}

              {selectedDate && selectedSlot && (
                <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#1a9e72", textTransform: "uppercase", marginBottom: 6 }}>Sélection actuelle</div>
                  <div style={{ fontSize: 13, color: "#F9FAFB", fontWeight: 600 }}>{formatDateFull(selectedDate)}</div>
                  <div style={{ fontSize: 13, color: "#6EE7B7" }}>{formatTime(selectedSlot.start)} — {formatTime(selectedSlot.end)}</div>
                </div>
              )}
            </div>

            {/* Colonne droite — Calendrier + créneaux / Formulaire */}
            <div>
              {step === 1 && (
                <div className="pp-book-card" style={{ ...styles.card, padding: 32 }}>
                  <h3 style={{ margin: "0 0 24px", fontSize: 19, fontWeight: 700, color: "#F9FAFB" }}>Choisissez une date et un créneau</h3>

                  <div className="pp-book-cal-grid" style={{ display: "grid", gridTemplateColumns: selectedDate ? "1fr 1fr" : "1fr", gap: 32 }}>
                    {/* Calendrier */}
                    <div>
                      <BookingCalendar
                        availableDays={availableDays}
                        selectedDate={selectedDate}
                        onSelect={handleDateSelect}
                        onMonthChange={(m) => { setCurrentMonth(m); loadAvailableDays(m); }}
                        initialMonth={currentMonth}
                      />
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748B" }}>
                        <Globe size={11} />
                        Europe/Paris (UTC+2)
                      </div>
                    </div>

                    {/* Créneaux horaires */}
                    {selectedDate && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#F9FAFB", marginBottom: 12 }}>
                          {formatDateFull(selectedDate)}
                        </div>
                        {slotsLoading ? (
                          <div style={{ textAlign: "center", padding: 20, color: "#64748B", fontSize: 13 }}>Chargement…</div>
                        ) : slots.length === 0 ? (
                          <div style={{ textAlign: "center", padding: 20, color: "#64748B", fontSize: 13 }}>
                            <AlertCircle size={20} style={{ display: "block", margin: "0 auto 8px" }} />
                            Aucun créneau disponible ce jour
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 340, overflowY: "auto" }}>
                            {slots.map((s, i) => {
                              const isSelected = selectedSlot?.start === s.start;
                              return (
                                <button
                                  key={i}
                                  onClick={() => setSelectedSlot(s)}
                                  style={{
                                    padding: "14px 20px",
                                    borderRadius: 9,
                                    border: isSelected ? "1px solid #34D399" : "1px solid rgba(255,255,255,0.1)",
                                    background: isSelected ? "#1a9e72" : "rgba(255,255,255,0.04)",
                                    color: isSelected ? "#fff" : "#94A3B8",
                                    cursor: "pointer",
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textAlign: "center",
                                    transition: "all 0.12s",
                                  }}
                                >
                                  {s.label}
                                  {isSelected && <Check size={13} style={{ marginLeft: 6, verticalAlign: "middle" }} />}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {selectedSlot && (
                          isRescheduleAction ? (
                            <button
                              onClick={handleRescheduleSubmit}
                              disabled={rescheduling}
                              style={{ ...styles.btnPrimary, marginTop: 16, opacity: rescheduling ? 0.6 : 1 }}
                            >
                              {rescheduling ? "Replanification en cours…" : "✅ Confirmer la replanification"}
                            </button>
                          ) : (
                            <button
                              onClick={() => setStep(2)}
                              style={{ ...styles.btnPrimary, marginTop: 16 }}
                            >
                              Continuer →
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div style={{ ...styles.card, padding: 28 }}>
                  <button
                    onClick={() => setStep(1)}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#64748B", fontSize: 13, marginBottom: 20, padding: 0 }}
                  >
                    <ArrowLeft size={14} /> Retour
                  </button>

                  <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#F9FAFB" }}>Vos informations</h3>

                  <div className="pp-book-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={styles.label}>Nom complet *</label>
                      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jean Dupont" style={styles.input} />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={styles.label}>Adresse email *</label>
                      <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean@exemple.fr" style={styles.input} />
                    </div>
                    <div>
                      <label style={styles.label}>Téléphone *</label>
                      <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+33 6 12 34 56 78" style={styles.input} />
                    </div>
                    <div>
                      <label style={styles.label}>Société (optionnel)</label>
                      <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Votre entreprise" style={styles.input} />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={styles.label}>Message (optionnel)</label>
                      <textarea
                        value={form.message}
                        onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                        placeholder="Partagez le contexte de votre demande…"
                        rows={3}
                        style={{ ...styles.input, resize: "vertical" }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={!form.name || !form.email || !form.phone || submitting}
                    style={{ ...styles.btnPrimary, marginTop: 20, opacity: (!form.name || !form.email || submitting) ? 0.6 : 1 }}
                  >
                    {submitting ? "Confirmation en cours…" : "✅ Confirmer le rendez-vous"}
                  </button>

                  <p style={{ fontSize: 11, color: "#475569", textAlign: "center", marginTop: 12 }}>
                    Un email de confirmation vous sera envoyé automatiquement.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", marginTop: 40, fontSize: 11, color: "#334155" }}>
        Propulsé par <strong style={{ color: "#1a9e72" }}>ExecPro360</strong> · © {COPYRIGHT_YEAR} {PUBLISHER_LABEL}
      </div>
    </div>
  );
}
